import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, PenTool, Upload, DollarSign, BarChart3, Shield, Zap, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * ForAuthorsModal - Informational modal about NewAudia for Authors
 * Displayed when clicking "Learn More" on the Authors card on the landing page
 */
const ForAuthorsModal = ({ isOpen, onClose, onGetStarted }) => {
  const navigate = useNavigate();
  
  const handleGetStarted = () => {
    onClose(); // Close the modal first
    navigate('/contact/authors'); // Navigate to author contact page
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 border-blue-500/30 text-slate-100">
        <DialogHeader className="border-b border-blue-500/20 pb-4">
          <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <PenTool className="w-6 h-6 text-blue-400" />
            </div>
            NewAudia for Authors
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Hero Statement */}
          <div className="text-center py-4">
            <h3 className="text-xl font-semibold text-blue-300 mb-2">
              Your Words Deserve to Be Heard
            </h3>
            <p className="text-slate-300">
              Transform your manuscript into an engaging audio experience — without the traditional barriers of audiobook production.
            </p>
          </div>

          {/* Key Benefits */}
          <div className="grid gap-4">
            <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Upload className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">First Manuscript Free</h4>
                  <p className="text-sm text-slate-400">
                    Your first manuscript upload is free. Additional manuscript uploads are subject to an upload fee per manuscript. No subscriptions.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">AI-Powered Audio</h4>
                  <p className="text-sm text-slate-400">
                    Advanced text-to-speech technology delivers natural, expressive narration. Readers can listen anywhere, anytime — just like a traditional audiobook.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Engagement-Based Earnings</h4>
                  <p className="text-sm text-slate-400">
                    Earn commissions based on actual reader engagement — not retail sales. When readers listen, you earn. It's that simple.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">Real-Time Analytics</h4>
                  <p className="text-sm text-slate-400">
                    See exactly how readers engage with your work. Track listening time, completion rates, and which chapters resonate most.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-1">You Keep Your Rights</h4>
                  <p className="text-sm text-slate-400">
                    NewAudia is non-exclusive. Your copyright remains yours. Upload today, remove tomorrow — no long-term contracts, no lock-in.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50">
            <h4 className="font-semibold text-white mb-4 text-center">How It Works</h4>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center">
              <div className="flex-1">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">1</div>
                <p className="text-sm text-slate-300">Upload your EPUB</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 hidden sm:block" />
              <div className="flex-1">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">2</div>
                <p className="text-sm text-slate-300">We generate audio</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 hidden sm:block" />
              <div className="flex-1">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">3</div>
                <p className="text-sm text-slate-300">Readers listen & engage</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-500 hidden sm:block" />
              <div className="flex-1">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 text-white font-bold">4</div>
                <p className="text-sm text-slate-300">You earn commissions</p>
              </div>
            </div>
          </div>

          {/* FAQ Preview */}
          <div className="space-y-3">
            <h4 className="font-semibold text-white">Common Questions</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400"><span className="text-slate-200">What formats do you accept?</span> — Currently we accept EPUB and Microsoft Word (.docx or .doc). Support for other formats coming soon.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400"><span className="text-slate-200">How much can I earn?</span> — Earnings depend on reader engagement. The more your book is listened to and interacted with, the more you earn.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400"><span className="text-slate-200">Is there an approval process?</span> — Yes, all submissions go through content review to ensure quality and compliance.</p>
              </div>
            </div>
          </div>

          {/* Development Notice */}
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-xl p-4">
            <p className="text-sm text-amber-200 text-center">
              <strong>Note:</strong> These modules are currently in development and will be launched soon. Authors interested in submitting their books immediately are urged to contact NewAudia today at <span className="font-semibold">(719) 205-6381</span>.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-blue-500/20">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Close
          </Button>
          <Button
            onClick={handleGetStarted}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            data-testid="author-get-started-btn"
          >
            Get Started as an Author
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForAuthorsModal;
